import { el } from '../lib/dom.js';
import { Sprite } from './Sprite.js';
import { TypeChip } from './TypeChip.js';
import { entry, toggle } from '../store/selection.js';
import { typeHex } from '../lib/type-color.js';
import { typesForGen } from '../lib/type-resolve.js';
import { prefersReducedMotion } from '../store/settings.js';
import { buzz } from '../lib/haptics.js';
import { prettify } from '../data/pokedex-resolver.js';

/**
 * One Pokémon in the dex feed. Tinted by its primary type: a soft top-down
 * gradient, a type-colored spotlight behind an oversized sprite that lifts
 * above the card, a number chip, and a big ghost number in the corner.
 */
export function Card(
  species,
  number,
  { spriteStyle = 'official', versionGroup, gen = 9, boxed = false, metric = null } = {},
) {
  const state = entry(species.id);
  const types = typesForGen(species, gen);
  const mainType = types[0] || 'normal';
  const num = String(number ?? species.id).padStart(3, '0');
  const data = { type: mainType, sprite: spriteStyle };
  if (boxed) data.boxed = '1';

  const caughtBtn = el('button', {
    class: 'card__caught',
    type: 'button',
    title: 'Toggle caught',
    'aria-pressed': String(state.caught),
    onclick: (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggle(species.id, 'caught');
      const next = entry(species.id);
      caughtBtn.setAttribute('aria-pressed', String(next.caught));
      card.classList.toggle('is-caught', next.caught);
      if (next.caught) {
        buzz();
        if (!prefersReducedMotion()) {
          caughtBtn.animate(
            [{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }],
            { duration: 260, easing: 'cubic-bezier(.34,1.4,.64,1)' },
          );
        }
      }
    },
  });

  const art = el(
    'div',
    { class: 'card__art' },
    Sprite(species.id, { style: spriteStyle, versionGroup, alt: species.name, size: 116 }),
  );

  const card = el(
    'a',
    {
      class: `card${state.caught ? ' is-caught' : ''}`,
      href: `#/pokemon/${species.id}`,
      dataset: data,
      style: `--type-main:${typeHex(mainType)}`,
      onclick: () => {
        // Tag this sprite so the router's view transition morphs it into
        // the detail hero.
        for (const n of document.querySelectorAll('.card__art.is-morph'))
          n.classList.remove('is-morph');
        art.classList.add('is-morph');
      },
    },
    el('span', { class: 'card__ghost', 'aria-hidden': 'true' }, num),
    el('span', { class: 'card__spot', 'aria-hidden': 'true' }),
    state.note && state.note.trim()
      ? el('span', { class: 'card__notemark', title: 'Has a note', 'aria-hidden': 'true' }, '📝')
      : null,
    art,
    caughtBtn,
    el(
      'div',
      { class: 'card__body' },
      el('span', { class: 'card__num' }, `#${num}`),
      el('span', { class: 'card__name' }, species.name.replace(/-/g, ' ')),
      el('span', { class: 'card__types' }, ...types.map(TypeChip)),
      (() => {
        const n = (species.forms?.length || 0) + (species.cosmeticForms?.length || 0);
        return n ? el('span', { class: 'card__forms' }, `+${n} form${n > 1 ? 's' : ''}`) : null;
      })(),
      (() => {
        const ex = versionGroup && versionGroup !== 'all' && species.exclusiveIn?.[versionGroup];
        return ex
          ? el(
              'span',
              { class: 'card__excl', title: 'Version exclusive (wild)' },
              `${ex.map(prettify).join(' & ')} only`,
            )
          : null;
      })(),
      metric ? el('span', { class: 'card__metric' }, metric) : null,
    ),
  );

  return card;
}
